import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useInView } from 'react-intersection-observer';
import { useLocation, useNavigate } from 'react-router-dom';
import SidebarNav from '../components/SidebarNav';

const DeveloperPage = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();

    // --- State Variables ---
    const [messages, setMessages] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [loadingMessages, setLoadingMessages] = useState(true);
    const [loadingAnalytics, setLoadingAnalytics] = useState(true);
    const [error, setError] = useState(null);

    // --- Theming ---
    const textPrimary = "text-silver-light";
    const textSecondary = "text-silver-medium/80";
    const accentColor = "text-blue-400";
    const cardBg = "bg-gray-900/60";
    const contentBg = "bg-gray-950/50";
    const borderColor = "border-gray-700/30";

    // --- Fetching Logic ---
    useEffect(() => {
        const fetchData = async () => {
            // Fetch Messages
            setLoadingMessages(true);
            try {
                // Use relative path if backend is proxied, otherwise full URL
                const messagesRes = await axios.get('/api/messages');
                if (messagesRes.data && messagesRes.data.success) {
                    setMessages(messagesRes.data.data);
                } else {
                    throw new Error(messagesRes.data.message || 'Failed to fetch messages');
                }
            } catch (err) {
                setError(`Error fetching messages: ${err.message}`);
                console.error("Message Fetch Error:", err);
            } finally {
                setLoadingMessages(false);
            }

            // Fetch Analytics
            setLoadingAnalytics(true);
            try {
                const analyticsRes = await axios.get('/api/analytics/summary');
                if (analyticsRes.data && analyticsRes.data.success) {
                    setAnalytics(analyticsRes.data.data);
                } else {
                    throw new Error(analyticsRes.data.message || 'Failed to fetch analytics');
                }
            } catch (err) {
                setError(prev => prev ? `${prev}\nError fetching analytics: ${err.message}` : `Error fetching analytics: ${err.message}`);
                console.error("Analytics Fetch Error:", err);
            } finally {
                setLoadingAnalytics(false);
            }
        };

        fetchData();
    }, []);

    // --- Intersection Observer Animations ---
    const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
    const [contentRef, contentInView] = useInView({ triggerOnce: true, threshold: 0.1 });

    return (
        <div className="bg-black min-h-screen font-sans">
            {/* Sidebar */}
            <SidebarNav currentPath={location.pathname} />

            {/* Main Content Area */}
            <div className="ml-64">
                {/* Hero Section */}
                <section
                    ref={heroRef}
                    className="pt-36 pb-16 bg-black"
                >
                    <div className="container mx-auto px-6">
                        <div
                            className={`max-w-3xl mx-auto text-center transition-all duration-1000 transform ${heroInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                        >
                            <h1 className={`text-5xl md:text-6xl font-serif font-medium mb-6 ${textPrimary}`}>
                                {t('developerPage.dashboardTitle', 'Developer Dashboard')}
                            </h1>
                            <p className={`text-lg md:text-xl ${textSecondary} mb-8 leading-relaxed`}>
                                {t('developerPage.dashboardSubtitle', 'Access site analytics and manage contact messages.')}
                            </p>
                            <div className="mt-3 w-40 h-1 mx-auto bg-gradient-to-r from-transparent via-blue-400 to-transparent opacity-50"></div>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section
                    ref={contentRef}
                    className={`py-8 transition-all duration-1000 transform ${contentInView ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}
                >
                    <div className="container mx-auto px-6 space-y-8">
                        {/* Display Error if any */}
                        {error && (
                            <div className="max-w-5xl mx-auto bg-red-900/20 border border-red-700/30 text-red-300 px-5 py-4 rounded-lg mb-6">
                                <div className="flex items-center space-x-3 mb-2">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <h3 className="font-bold text-sm">Error Loading Data</h3>
                                </div>
                                <pre className="text-xs whitespace-pre-wrap">{error}</pre>
                            </div>
                        )}

                        {/* Dashboard Cards */}
                        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Analytics Overview Card */}
                            <div
                                className={`${cardBg} rounded-lg border ${borderColor} p-6 shadow-lg backdrop-blur-sm hover:shadow-xl transition-all duration-300 hover:border-blue-900/30 cursor-pointer`}
                                onClick={() => navigate('/developer/analytics')}
                            >
                                <div className="flex items-center mb-4">
                                    <div className="rounded-full bg-blue-900/30 p-3 mr-4">
                                        <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
                                        </svg>
                                    </div>
                                    <h2 className={`text-xl font-medium ${textPrimary}`}>Site Analytics</h2>
                                </div>

                                <p className={`${textSecondary} mb-5`}>View detailed insights into website traffic, user engagement, and visitor locations.</p>

                                {loadingAnalytics ? (
                                    <div className="animate-pulse h-8 bg-gray-800/50 rounded w-1/3 mt-2"></div>
                                ) : analytics ? (
                                    <div className="text-sm grid grid-cols-2 gap-3">
                                        <div className={`${contentBg} p-3 rounded border ${borderColor}`}>
                                            <div className={textSecondary}>Total Hits</div>
                                            <div className={`text-xl font-medium ${accentColor}`}>
                                                {analytics.locationData ? analytics.locationData.reduce((sum, loc) => sum + loc.count, 0) : 0}
                                            </div>
                                        </div>

                                        <div className={`${contentBg} p-3 rounded border ${borderColor}`}>
                                            <div className={textSecondary}>Countries</div>
                                            <div className={`text-xl font-medium ${accentColor}`}>
                                                {analytics.locationData ? analytics.locationData.length : 0}
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <p className="text-sm text-gray-500">No analytics data available yet.</p>
                                )}

                                <button className="mt-6 flex items-center text-sm font-medium text-blue-400 hover:text-blue-300 focus:outline-none">
                                    View Analytics
                                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                                    </svg>
                                </button>
                            </div>

                            {/* Messages Overview Card */}
                            <div
                                className={`${cardBg} rounded-lg border ${borderColor} p-6 shadow-lg backdrop-blur-sm hover:shadow-xl transition-all duration-300 hover:border-blue-900/30 cursor-pointer`}
                                onClick={() => navigate('/developer/messages')}
                            >
                                <div className="flex items-center mb-4">
                                    <div className="rounded-full bg-blue-900/30 p-3 mr-4">
                                        <svg className="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
                                        </svg>
                                    </div>
                                    <h2 className={`text-xl font-medium ${textPrimary}`}>Contact Messages</h2>
                                </div>

                                <p className={`${textSecondary} mb-5`}>View and respond to messages received through the website contact form.</p>

                                {loadingMessages ? (
                                    <div className="animate-pulse h-8 bg-gray-800/50 rounded w-1/3 mt-2"></div>
                                ) : (
                                    <div className={`${contentBg} p-3 rounded border ${borderColor}`}>
                                        <div className={textSecondary}>Total Messages</div>
                                        <div className={`text-xl font-medium ${accentColor}`}>
                                            {messages.length || 0}
                                        </div>
                                    </div>
                                )}

                                <button className="mt-6 flex items-center text-sm font-medium text-blue-400 hover:text-blue-300 focus:outline-none">
                                    View Messages
                                    <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3"></path>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default DeveloperPage; 